import React, { useState } from "react";
import { useSelector } from "react-redux";

import { useContentSize, saveContentSize } from "../../hooks/useContentSize";
import { finishOnboardingUser } from "../../utils/userEndpoints";
import { isLinkedInUrlValid } from "../../utils/validationUtils";
import { selectActivePixelStreamingExperience } from "../../store/userSlice";
import FinalizeCreateUserView from "./FinalizeCreateUserView";
import LoadingUserComponent from "./LoadingUser";
import { selectOrganizationId } from "../../store/experienceSlice";

interface Props {
  onSubmitSuccess: (user: any) => void;
  resLinkParamsId?: string;
  botName?: string;
  propFirstName?: string;
  middleName?: string;
  propLastName?: string;
  profilePictureUrl?: string;
}

const FinalizeCreateUserContainer: React.FC<Props> = ({ onSubmitSuccess, resLinkParamsId, botName, propFirstName, middleName, propLastName, profilePictureUrl }) => {
  const [firstName, setFirstName] = useState(propFirstName || "");
  const [lastName, setLastName] = useState(propLastName || "");
  const [linkedInUrl, setLinkedInUrl] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { ref: contentRef, size: contentSize } = useContentSize();
  const activeExperience = useSelector(selectActivePixelStreamingExperience);
  const reduxOrganizationId = useSelector(selectOrganizationId);

  const saveCardSize = () => {
    saveContentSize(contentRef);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (activeExperience?.linkedin_url_required) {
      if (!isLinkedInUrlValid(linkedInUrl)) {
        setStatusMessage(
          "Please enter your public profile LinkedIn url. It should start with www.linkedin.com/in/..."
        );
        return;
      }
    }

    saveCardSize();
    setLoading(true);
    setStatusMessage(null);

    try {
      const user = await finishOnboardingUser(
        firstName.trim(),
        middleName?.trim(),
        lastName.trim(),
        linkedInUrl?.trim() || undefined,
        resLinkParamsId,
        reduxOrganizationId
      );
      onSubmitSuccess(user);
    } catch (err: any) {
      if (err.status === 422) {
        setStatusMessage(err.message || "Invalid LinkedIn URL. Please check and try again.");
      } else if (err.status === 400) {
        setStatusMessage(err.message || "Bad request. Please check your input and try again.");
      } else {
        setStatusMessage(err.message || "Failed to create user. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: contentSize?.height || "auto",
          minWidth: contentSize?.width || "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <LoadingUserComponent />
      </div>
    );
  }

  return (
    <div ref={contentRef} className="user-finalize-container">
      <FinalizeCreateUserView
        profilePictureUrl={profilePictureUrl}
        linkedInUrl={linkedInUrl}
        firstName={firstName || ""}
        lastName={lastName || ""}
        starting={loading}
        statusMessage={statusMessage}
        requestLinkedIn={activeExperience?.linkedin_url_requested || false}
        allowNameEditing={true}
        isSubmitDisabled={loading || firstName.trim().length === 0}
        botName={botName}
        onLinkedInUrlChange={(e) => setLinkedInUrl(e.target.value)}
        onFirstNameChange={(e) => setFirstName(e.target.value)}
        onLastNameChange={(e) => setLastName(e.target.value)}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default FinalizeCreateUserContainer;
